import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock,
  Calendar,
  X,
  Tool,
  History,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

const maintenanceTypes = [
  { id: 'routine', label: 'Routine', color: 'bg-blue-100 text-blue-700' },
  { id: 'repair', label: 'Repair', color: 'bg-red-100 text-red-700' },
  { id: 'inspection', label: 'Inspection', color: 'bg-green-100 text-green-700' },
  { id: 'calibration', label: 'Calibration', color: 'bg-purple-100 text-purple-700' }
];

const statusConfig = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700', icon: Wrench },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

export const Maintenance = () => {
  const { api } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [dueSoon, setDueSoon] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('schedules');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, dueSoonRes, equipRes, empRes] = await Promise.all([
        api.get('/maintenance/schedules'),
        api.get('/maintenance/due-soon'),
        api.get('/inventory'),
        api.get('/employees')
      ]);
      setSchedules(schedulesRes.data);
      setDueSoon(dueSoonRes.data);
      setEquipment(equipRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
      toast.error('Failed to load maintenance data');
    } finally {
      setLoading(false);
    }
  };

  const createSchedule = async (data) => {
    try {
      await api.post('/maintenance/schedule', data);
      toast.success('Maintenance scheduled');
      fetchData();
      setShowAddModal(false);
    } catch (error) {
      toast.error('Failed to schedule maintenance');
    }
  };

  const completeMaintenance = async (scheduleId, data) => {
    try {
      const formData = new FormData();
      if (data.actual_hours) formData.append('actual_hours', data.actual_hours);
      formData.append('cost', data.cost || 0);
      if (data.notes) formData.append('notes', data.notes);
      
      await api.patch(`/maintenance/schedules/${scheduleId}/complete`, formData);
      toast.success('Maintenance completed');
      fetchData();
      setShowCompleteModal(false);
      setSelectedSchedule(null);
    } catch (error) {
      toast.error('Failed to complete maintenance');
    }
  };

  const viewHistory = async (equipmentId) => {
    try {
      const response = await api.get(`/maintenance/logs/${equipmentId}`);
      setMaintenanceLogs(response.data);
      const eq = equipment.find(e => e.id === equipmentId);
      setSelectedEquipment(eq);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Failed to load maintenance history');
    }
  };

  const filteredSchedules = schedules.filter(s => {
    const matchesSearch = s.equipment_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          s.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !filterStatus || s.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: schedules.length,
    scheduled: schedules.filter(s => s.status === 'scheduled').length,
    overdue: schedules.filter(s => s.status === 'overdue').length,
    completed: schedules.filter(s => s.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Equipment Maintenance</h1>
          <p className="text-gray-500 mt-1">Schedule and track equipment maintenance</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase hover:bg-[#B09265]"
          data-testid="schedule-maintenance-btn"
        >
          <Plus className="w-4 h-4" />
          Schedule Maintenance
        </button>
      </div>

      {/* Alerts for Due Soon */}
      {dueSoon.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-50 border border-yellow-200 p-4"
        >
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Maintenance Due Soon</p>
              <p className="text-sm text-yellow-700 mt-1">
                {dueSoon.length} maintenance task{dueSoon.length > 1 ? 's' : ''} due within 7 days
              </p>
              <div className="mt-2 space-y-1">
                {dueSoon.slice(0, 3).map(s => (
                  <p key={s.id} className="text-sm text-yellow-600">
                    • {s.equipment_name} - {s.maintenance_type} ({new Date(s.scheduled_date).toLocaleDateString()})
                  </p>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-100 p-4">
          <p className="text-xs font-semibold uppercase text-gray-400 mb-1">Total Scheduled</p>
          <p className="font-heading text-2xl">{stats.total}</p>
        </div>
        <div className="bg-blue-50 border border-blue-100 p-4">
          <p className="text-xs font-semibold uppercase text-blue-600 mb-1">Upcoming</p>
          <p className="font-heading text-2xl text-blue-700">{stats.scheduled}</p>
        </div>
        <div className="bg-red-50 border border-red-100 p-4">
          <p className="text-xs font-semibold uppercase text-red-600 mb-1">Overdue</p>
          <p className="font-heading text-2xl text-red-700">{stats.overdue}</p>
        </div>
        <div className="bg-green-50 border border-green-100 p-4">
          <p className="text-xs font-semibold uppercase text-green-600 mb-1">Completed</p>
          <p className="font-heading text-2xl text-green-700">{stats.completed}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('schedules')}
            className={`py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'schedules' ? 'border-[#C6A87C] text-[#C6A87C]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Schedules
          </button>
          <button
            onClick={() => setActiveTab('equipment')}
            className={`py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'equipment' ? 'border-[#C6A87C] text-[#C6A87C]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Equipment Status
          </button>
        </nav>
      </div>

      {activeTab === 'schedules' && (
        <>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search maintenance..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white min-w-[150px]"
            >
              <option value="">All Statuses</option>
              {Object.entries(statusConfig).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Schedules List */}
          <div className="bg-white border border-gray-100">
            {filteredSchedules.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {filteredSchedules.map(schedule => {
                  const statusInfo = statusConfig[schedule.status] || statusConfig.scheduled;
                  const StatusIcon = statusInfo.icon;
                  const typeInfo = maintenanceTypes.find(t => t.id === schedule.maintenance_type);
                  
                  return (
                    <div key={schedule.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <Wrench className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <h3 className="font-medium">{schedule.equipment_name}</h3>
                            <p className="text-sm text-gray-500">{schedule.description}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className={`px-2 py-0.5 text-xs ${typeInfo?.color}`}>
                                {typeInfo?.label}
                              </span>
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(schedule.scheduled_date).toLocaleDateString()}
                              </span>
                              {schedule.assigned_to && (
                                <span className="text-xs text-gray-400">
                                  Assigned: {schedule.assigned_to}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 text-xs font-semibold flex items-center gap-1 ${statusInfo.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusInfo.label}
                          </span>
                          {schedule.status !== 'completed' && (
                            <button
                              onClick={() => { setSelectedSchedule(schedule); setShowCompleteModal(true); }}
                              className="px-3 py-1 text-xs font-semibold text-green-600 border border-green-300 hover:bg-green-50"
                            >
                              Complete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No maintenance schedules found</p>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'equipment' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {equipment.map(eq => {
            const lastMaintenance = eq.last_maintenance_date;
            const eqSchedules = schedules.filter(s => s.equipment_id === eq.id && s.status !== 'completed');
            const hasOverdue = eqSchedules.some(s => s.status === 'overdue');
            
            return (
              <div key={eq.id} className={`bg-white border p-4 ${hasOverdue ? 'border-red-300' : 'border-gray-100'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium">{eq.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{eq.category}</p>
                  </div>
                  {hasOverdue && (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  )}
                </div>
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Last Maintenance:</span>
                    <span>{lastMaintenance ? new Date(lastMaintenance).toLocaleDateString() : 'Never'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Pending Tasks:</span>
                    <span className={eqSchedules.length > 0 ? 'text-yellow-600' : 'text-green-600'}>
                      {eqSchedules.length}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => viewHistory(eq.id)}
                    className="flex-1 py-2 text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 flex items-center justify-center gap-1"
                  >
                    <History className="w-3 h-3" />
                    History
                  </button>
                  <button
                    onClick={() => { setSelectedEquipment(eq); setShowAddModal(true); }}
                    className="flex-1 py-2 text-xs font-semibold text-[#C6A87C] border border-[#C6A87C] hover:bg-[#C6A87C]/5 flex items-center justify-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Schedule
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Schedule Modal */}
      <AnimatePresence>
        {showAddModal && (
          <ScheduleModal
            equipment={equipment}
            employees={employees}
            preselectedEquipment={selectedEquipment}
            onClose={() => { setShowAddModal(false); setSelectedEquipment(null); }}
            onCreate={createSchedule}
          />
        )}
      </AnimatePresence>

      {/* Complete Modal */}
      <AnimatePresence>
        {showCompleteModal && selectedSchedule && (
          <CompleteModal
            schedule={selectedSchedule}
            onClose={() => { setShowCompleteModal(false); setSelectedSchedule(null); }}
            onComplete={(data) => completeMaintenance(selectedSchedule.id, data)}
          />
        )}
      </AnimatePresence>

      {/* History Modal */}
      <AnimatePresence>
        {showHistoryModal && selectedEquipment && (
          <HistoryModal
            equipment={selectedEquipment}
            logs={maintenanceLogs}
            onClose={() => { setShowHistoryModal(false); setSelectedEquipment(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Schedule Modal
const ScheduleModal = ({ equipment, employees, preselectedEquipment, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    equipment_id: preselectedEquipment?.id || '',
    maintenance_type: 'routine',
    description: '',
    scheduled_date: '',
    estimated_duration_hours: 1,
    assigned_to: '',
    notes: ''
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="font-heading text-xl">Schedule Maintenance</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onCreate(formData); }} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Equipment *</label>
            <select value={formData.equipment_id} onChange={(e) => setFormData({ ...formData, equipment_id: e.target.value })} required className="w-full px-4 py-3 border border-gray-200 bg-white">
              <option value="">Select equipment...</option>
              {equipment.map(eq => <option key={eq.id} value={eq.id}>{eq.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Type *</label>
              <select value={formData.maintenance_type} onChange={(e) => setFormData({ ...formData, maintenance_type: e.target.value })} className="w-full px-4 py-3 border border-gray-200 bg-white">
                {maintenanceTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Date *</label>
              <input type="date" value={formData.scheduled_date} onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} required className="w-full px-4 py-3 border border-gray-200" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Description *</label>
            <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required className="w-full px-4 py-3 border border-gray-200" placeholder="Brief description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Est. Hours</label>
              <input type="number" step="0.5" value={formData.estimated_duration_hours} onChange={(e) => setFormData({ ...formData, estimated_duration_hours: parseFloat(e.target.value) })} className="w-full px-4 py-3 border border-gray-200" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Assign To</label>
              <select value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })} className="w-full px-4 py-3 border border-gray-200 bg-white">
                <option value="">Unassigned</option>
                {employees.map(emp => <option key={emp.id} value={emp.name}>{emp.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-6 py-3 border text-gray-600 text-xs font-semibold uppercase">Cancel</button>
            <button type="submit" className="px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265]">Schedule</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Complete Modal
const CompleteModal = ({ schedule, onClose, onComplete }) => {
  const [formData, setFormData] = useState({ actual_hours: schedule.estimated_duration_hours, cost: 0, notes: '' });
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="font-heading text-xl">Complete Maintenance</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onComplete(formData); }} className="p-6 space-y-4">
          <div className="bg-gray-50 p-4 mb-4">
            <p className="font-medium">{schedule.equipment_name}</p>
            <p className="text-sm text-gray-500">{schedule.description}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Actual Hours</label>
              <input type="number" step="0.5" value={formData.actual_hours} onChange={(e) => setFormData({ ...formData, actual_hours: parseFloat(e.target.value) })} className="w-full px-4 py-3 border border-gray-200" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Cost ($)</label>
              <input type="number" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })} className="w-full px-4 py-3 border border-gray-200" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Notes</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full px-4 py-3 border border-gray-200 resize-none" placeholder="Work performed, parts replaced..." />
          </div>
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-6 py-3 border text-gray-600 text-xs font-semibold uppercase">Cancel</button>
            <button type="submit" className="px-6 py-3 bg-green-600 text-white text-xs font-semibold uppercase hover:bg-green-700">Mark Complete</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// History Modal
const HistoryModal = ({ equipment, logs, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
        <h2 className="font-heading text-xl">Maintenance History: {equipment.name}</h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6">
        {logs.length > 0 ? (
          <div className="space-y-4">
            {logs.map(log => (
              <div key={log.id} className="border-l-4 border-[#C6A87C] pl-4 py-2">
                <div className="flex justify-between items-start">
                  <p className="font-medium capitalize">{log.maintenance_type}</p>
                  <span className="text-sm text-gray-500">{new Date(log.completed_date).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{log.notes || 'No notes'}</p>
                <div className="flex gap-4 mt-2 text-xs text-gray-400">
                  <span>Duration: {log.duration_hours}h</span>
                  <span>Cost: ${log.cost}</span>
                  <span>By: {log.completed_by}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-400 py-8">No maintenance history</p>
        )}
      </div>
    </motion.div>
  </motion.div>
);
